import React, { useEffect, useMemo, useState } from 'react';
import chatApi from '../../api/chatApi';
import './DocumentEditCard.css';

const RETRY_DELAY_MS = 1200;

const mapApiError = (status) => {
    if (status === 400) return 'Некорректные параметры запроса';
    if (status === 403) return 'Нет доступа к этой версии файла';
    if (status === 404) return 'Версия не найдена';
    return 'Не удалось получить данные. Повторите позже';
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const withSingleRetry = async (requestFn) => {
    try {
        return await requestFn();
    } catch (firstErr) {
        await wait(RETRY_DELAY_MS);
        return requestFn();
    }
};

const safeDecode = (value) => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

const getFileNameFromUrl = (url) => {
    if (!url) return 'document';

    const clean = url.split('?')[0];
    const parts = clean.split('/');
    const fileName = parts[parts.length - 1] || 'document';
    return safeDecode(fileName);
};

const toEditedFileMeta = (raw) => {
    if (!raw) return null;

    return {
        sessionId: raw.sessionId || raw.session_id || '',
        version: Number(raw.version || 1),
        originalFileUrl: raw.originalFileUrl || raw.original_file_url || raw.originalFile || raw.original_file || '',
        editedFileUrl: raw.editedFileUrl || raw.edited_file_url || raw.editedFile || raw.edited_file || '',
        reviewFileUrl: raw.reviewFileUrl || raw.review_file_url || raw.reviewFile || raw.review_file || '',
        protocolFileUrl: raw.protocolFileUrl || raw.protocol_file_url || raw.protocolFile || raw.protocol_file || '',
        createdAt: raw.createdAt || raw.created_at || '',
    };
};

const normalizeDocxParagraphs = (payload) => {
    const paragraphs = payload?.paragraphs;
    if (Array.isArray(paragraphs)) return paragraphs;

    if (typeof payload?.text === 'string' && payload.text.trim()) {
        return payload.text.split('\n');
    }

    return [];
};

const buildXlsxRows = (originalSnapshot, editedSnapshot) => {
    const map = new Map();

    (Array.isArray(originalSnapshot) ? originalSnapshot : []).forEach((item) => {
        const key = `${item.sheetName || ''}::${item.cell || ''}`;
        map.set(key, {
            sheetName: item.sheetName || '',
            cell: item.cell || '',
            original: item.value ?? '',
            edited: '',
        });
    });

    (Array.isArray(editedSnapshot) ? editedSnapshot : []).forEach((item) => {
        const key = `${item.sheetName || ''}::${item.cell || ''}`;
        const current = map.get(key) || {
            sheetName: item.sheetName || '',
            cell: item.cell || '',
            original: '',
            edited: '',
        };

        current.edited = item.value ?? '';
        map.set(key, current);
    });

    return Array.from(map.values());
};

const buildDocxCompareRows = (preview) => {
    const beforeRows = normalizeDocxParagraphs(preview?.original);
    const afterRows = normalizeDocxParagraphs(preview?.edited);

    const normalizeForParagraphMatch = (text) => String(text || '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

    const getWordTokens = (text) => {
        const normalized = normalizeForParagraphMatch(text);
        const words = normalized.match(/[A-Za-zА-Яа-яЁё0-9]+/g);
        return Array.isArray(words) ? words : [];
    };

    const paragraphSimilarity = (beforeText, afterText) => {
        const a = normalizeForParagraphMatch(beforeText);
        const b = normalizeForParagraphMatch(afterText);

        if (!a && !b) return 1;
        if (a === b) return 1;

        const aw = getWordTokens(a);
        const bw = getWordTokens(b);
        if (aw.length === 0 || bw.length === 0) return 0;

        const freqA = new Map();
        for (const w of aw) {
            freqA.set(w, (freqA.get(w) || 0) + 1);
        }

        let intersection = 0;
        for (const w of bw) {
            const cnt = freqA.get(w) || 0;
            if (cnt > 0) {
                intersection += 1;
                freqA.set(w, cnt - 1);
            }
        }

        return (2 * intersection) / (aw.length + bw.length);
    };

    const n = beforeRows.length;
    const m = afterRows.length;
    const maxParagraphMatrixCells = 30000;

    // Safety fallback for very large documents.
    if (n * m > maxParagraphMatrixCells) {
        const maxLen = Math.max(n, m);
        const rows = [];
        for (let i = 0; i < maxLen; i += 1) {
            const before = beforeRows[i] || '';
            const after = afterRows[i] || '';
            rows.push({
                index: i + 1,
                before,
                after,
                changed: before !== after,
            });
        }
        return rows;
    }

    const dp = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
    for (let i = 0; i <= n; i += 1) dp[i][0] = i;
    for (let j = 0; j <= m; j += 1) dp[0][j] = j;

    for (let i = 1; i <= n; i += 1) {
        for (let j = 1; j <= m; j += 1) {
            const deletion = dp[i - 1][j] + 1;
            const insertion = dp[i][j - 1] + 1;
            const substitutionCost = 1 - paragraphSimilarity(beforeRows[i - 1], afterRows[j - 1]);
            const substitution = dp[i - 1][j - 1] + substitutionCost;
            dp[i][j] = Math.min(deletion, insertion, substitution);
        }
    }

    const epsilon = 1e-9;
    const aligned = [];
    let i = n;
    let j = m;

    while (i > 0 || j > 0) {
        const canSubstitute = i > 0 && j > 0;
        const canDelete = i > 0;
        const canInsert = j > 0;

        if (canSubstitute) {
            const substitutionCost = 1 - paragraphSimilarity(beforeRows[i - 1], afterRows[j - 1]);
            const substitution = dp[i - 1][j - 1] + substitutionCost;
            if (Math.abs(dp[i][j] - substitution) < epsilon) {
                aligned.push({ before: beforeRows[i - 1] || '', after: afterRows[j - 1] || '' });
                i -= 1;
                j -= 1;
                continue;
            }
        }

        if (canDelete) {
            const deletion = dp[i - 1][j] + 1;
            if (Math.abs(dp[i][j] - deletion) < epsilon) {
                aligned.push({ before: beforeRows[i - 1] || '', after: '' });
                i -= 1;
                continue;
            }
        }

        if (canInsert) {
            aligned.push({ before: '', after: afterRows[j - 1] || '' });
            j -= 1;
            continue;
        }
    }

    return aligned.reverse().map((row, index) => ({
        index: index + 1,
        before: row.before,
        after: row.after,
        changed: row.before !== row.after,
    }));
};

const tokenizeForReview = (text) => {
    const normalized = String(text || '');
    const chunks = normalized.match(/\S+\s*/g);
    return Array.isArray(chunks) ? chunks : [];
};

const buildInlineReviewDiff = (beforeText, afterText) => {
    const beforeTokens = tokenizeForReview(beforeText);
    const afterTokens = tokenizeForReview(afterText);

    if (beforeTokens.length === 0 && afterTokens.length === 0) {
        return [];
    }

    if (beforeTokens.join('') === afterTokens.join('')) {
        return [{ type: 'same', text: afterTokens.join('') }];
    }

    const runWordLcsDiff = (leftTokens, rightTokens) => {
        const n = leftTokens.length;
        const m = rightTokens.length;
        const dp = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));

        for (let i = 1; i <= n; i += 1) {
            for (let j = 1; j <= m; j += 1) {
                if (leftTokens[i - 1] === rightTokens[j - 1]) {
                    dp[i][j] = dp[i - 1][j - 1] + 1;
                } else {
                    dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
                }
            }
        }

        const reversedOps = [];
        let i = n;
        let j = m;

        while (i > 0 || j > 0) {
            if (i > 0 && j > 0 && leftTokens[i - 1] === rightTokens[j - 1]) {
                reversedOps.push({ type: 'same', text: leftTokens[i - 1] });
                i -= 1;
                j -= 1;
                continue;
            }

            if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
                reversedOps.push({ type: 'added', text: rightTokens[j - 1] });
                j -= 1;
                continue;
            }

            if (i > 0) {
                reversedOps.push({ type: 'removed', text: leftTokens[i - 1] });
                i -= 1;
            }
        }

        return reversedOps.reverse();
    };

    const mergeOps = (ops) => {
        const merged = [];
        for (const op of ops) {
            if (!op?.text) continue;
            const last = merged[merged.length - 1];
            if (last && last.type === op.type) {
                last.text += op.text;
            } else {
                merged.push({ ...op });
            }
        }
        return merged;
    };

    // Protect UI against very large paragraphs: fallback to a compact representation.
    const maxMatrixCells = 45000;
    if (beforeTokens.length * afterTokens.length > maxMatrixCells) {
        let left = 0;
        let rightBefore = beforeTokens.length - 1;
        let rightAfter = afterTokens.length - 1;

        while (left <= rightBefore && left <= rightAfter && beforeTokens[left] === afterTokens[left]) {
            left += 1;
        }

        while (
            rightBefore >= left &&
            rightAfter >= left &&
            beforeTokens[rightBefore] === afterTokens[rightAfter]
        ) {
            rightBefore -= 1;
            rightAfter -= 1;
        }

        const prefix = beforeTokens.slice(0, left);
        const middleBefore = beforeTokens.slice(left, rightBefore + 1);
        const middleAfter = afterTokens.slice(left, rightAfter + 1);
        const suffix = beforeTokens.slice(rightBefore + 1);

        const middleCells = middleBefore.length * middleAfter.length;
        const middleMaxCells = 120000;

        const middleOps = middleCells > 0 && middleCells <= middleMaxCells
            ? runWordLcsDiff(middleBefore, middleAfter)
            : [
                ...(middleBefore.length > 0 ? [{ type: 'removed', text: middleBefore.join('') }] : []),
                ...(middleAfter.length > 0 ? [{ type: 'added', text: middleAfter.join('') }] : []),
            ];

        return mergeOps([
            ...(prefix.length > 0 ? [{ type: 'same', text: prefix.join('') }] : []),
            ...middleOps,
            ...(suffix.length > 0 ? [{ type: 'same', text: suffix.join('') }] : []),
        ]);
    }

    return mergeOps(runWordLcsDiff(beforeTokens, afterTokens));
};

const buildXlsxCompareRows = (preview) => {
    return buildXlsxRows(preview?.original?.snapshot, preview?.edited?.snapshot).map((row) => ({
        ...row,
        changed: String(row.original) !== String(row.edited),
    }));
};

const colLabelToNumber = (label) => {
    let result = 0;
    const normalized = String(label || '').toUpperCase();
    for (let i = 0; i < normalized.length; i += 1) {
        result = result * 26 + (normalized.charCodeAt(i) - 64);
    }
    return result;
};

const numberToColLabel = (number) => {
    let n = Number(number);
    let label = '';
    while (n > 0) {
        const rem = (n - 1) % 26;
        label = String.fromCharCode(65 + rem) + label;
        n = Math.floor((n - 1) / 26);
    }
    return label || 'A';
};

const parseCellAddress = (address) => {
    const match = String(address || '').toUpperCase().match(/^([A-Z]{1,3})(\d+)$/);
    if (!match) return null;
    return {
        colLabel: match[1],
        row: Number(match[2]),
        col: colLabelToNumber(match[1]),
    };
};

const buildSheetState = (snapshot) => {
    const sheets = new Map();

    for (const item of snapshot || []) {
        const sheetName = String(item.sheetName || 'Sheet1');
        const parsed = parseCellAddress(item.cell);
        if (!parsed) continue;

        if (!sheets.has(sheetName)) {
            sheets.set(sheetName, {
                cells: new Map(),
                maxRow: 1,
                maxCol: 1,
            });
        }

        const sheet = sheets.get(sheetName);
        const value = item.value == null ? '' : String(item.value);
        const cellAddress = String(item.cell || '').toUpperCase();
        sheet.cells.set(cellAddress, value);
        sheet.maxRow = Math.max(sheet.maxRow, parsed.row);
        sheet.maxCol = Math.max(sheet.maxCol, parsed.col);
    }

    return sheets;
};

const buildChangedCellsBySheet = (beforeSheets, afterSheets) => {
    const changedBySheet = new Map();
    const sheetNames = new Set([...beforeSheets.keys(), ...afterSheets.keys()]);

    for (const sheetName of sheetNames) {
        const before = beforeSheets.get(sheetName)?.cells || new Map();
        const after = afterSheets.get(sheetName)?.cells || new Map();
        const keys = new Set([...before.keys(), ...after.keys()]);
        const changed = new Set();

        for (const key of keys) {
            const b = before.get(key) || '';
            const a = after.get(key) || '';
            if (b !== a) {
                changed.add(key);
            }
        }

        changedBySheet.set(sheetName, changed);
    }

    return changedBySheet;
};

const getSheetNames = (preview) => {
    const before = buildSheetState(preview?.original?.snapshot);
    const after = buildSheetState(preview?.edited?.snapshot);
    return [...new Set([...before.keys(), ...after.keys()])];
};

const getContentDispositionFileName = (headers) => {
    const contentDisposition = headers?.['content-disposition'] || headers?.['Content-Disposition'];
    if (!contentDisposition) return null;

    const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
        return safeDecode(utfMatch[1]);
    }

    const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return asciiMatch?.[1] ? safeDecode(asciiMatch[1]) : null;
};

const triggerBrowserDownload = (blob, preferredName) => {
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = preferredName || 'document';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(downloadUrl);
};

const InlinePreview = ({
        loading,
        error,
        preview,
        selectedSheet,
        onSelectSheet,
        onDownloadEdited,
        onDownloadReview,
        onDownloadProtocol,
        editedDownloadLabel,
    }) => {
    if (loading) {
        return (
            <div className="docs-inline-preview">
                <p>Загружаем предпросмотр...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="docs-inline-preview">
                <p className="docs-edit-error">{error}</p>
            </div>
        );
    }

    if (!preview) {
        return (
            <div className="docs-inline-preview docs-inline-placeholder">
                <p>Предпросмотр ещё не загружен</p>
            </div>
        );
    }

    const ext = preview?.ext;
    const isDocx = ext === '.docx';
    const isXlsx = ext === '.xlsx';
    const docxRows = isDocx
        ? (
            Array.isArray(preview?.compareRows)
                ? preview.compareRows.map((row) => ({
                    ...row,
                    changed: row.changed ?? (row.before !== row.after),
                    inlineDiff: Array.isArray(row.inlineDiff)
                        ? row.inlineDiff
                        : buildInlineReviewDiff(row.before, row.after),
                }))
                : buildDocxCompareRows(preview).map((row) => ({
                    ...row,
                    inlineDiff: buildInlineReviewDiff(row.before, row.after),
                }))
        )
        : [];

    const docxChangedRows = isDocx
        ? docxRows.filter((row) => row.changed ?? (row.before !== row.after))
        : [];
    const xlsxRows = isXlsx ? buildXlsxCompareRows(preview) : [];
    const xlsxChangedRows = xlsxRows.filter((row) => row.changed);

    const beforeSheets = isXlsx ? buildSheetState(preview?.original?.snapshot) : new Map();
    const afterSheets = isXlsx ? buildSheetState(preview?.edited?.snapshot) : new Map();
    const changedBySheet = isXlsx ? buildChangedCellsBySheet(beforeSheets, afterSheets) : new Map();
    const sheetNames = isXlsx ? [...new Set([...beforeSheets.keys(), ...afterSheets.keys()])] : [];
    const activeSheet = sheetNames.includes(selectedSheet) ? selectedSheet : (sheetNames[0] || 'Sheet1');
    const beforeSheet = beforeSheets.get(activeSheet) || { cells: new Map(), maxRow: 1, maxCol: 1 };
    const afterSheet = afterSheets.get(activeSheet) || { cells: new Map(), maxRow: 1, maxCol: 1 };
    const changedCells = changedBySheet.get(activeSheet) || new Set();
    const maxRow = Math.max(beforeSheet.maxRow || 1, afterSheet.maxRow || 1, 1);
    const maxCol = Math.max(beforeSheet.maxCol || 1, afterSheet.maxCol || 1, 1);

    return (
        <div className="docs-inline-preview" aria-label="Предпросмотр изменений документа">
            {(isDocx || isXlsx) && (
                <div className="docs-file-window">
                    <div className="docs-file-window-topbar">
                        <p>{isDocx ? 'Документ DOCX' : 'Таблица XLSX'} · сравнение изменений</p>
                        <div className="topbar-download-actions">
                        {onDownloadEdited && (
                            <button
                                type="button"
                                className="topbar-download-btn"
                                onClick={onDownloadEdited}
                                aria-label="Скачать чистовой файл"
                            >
                                {editedDownloadLabel || 'Скачать чистовой'}
                            </button>
                        )}

                        {onDownloadReview && (
                            <button
                                type="button"
                                className="topbar-download-btn"
                                onClick={onDownloadReview}
                                aria-label="Скачать файл в режиме правки"
                            >
                                Скачать в режиме правки
                            </button>
                        )}

                        {onDownloadProtocol && (
                            <button
                                type="button"
                                className="topbar-download-btn"
                                onClick={onDownloadProtocol}
                                aria-label="Скачать протокол разногласий"
                            >
                                Скачать протокол разногласий
                            </button>
                        )}
                    </div>
                    </div>

                    {isDocx && (
                        <div className="docs-compare-list" data-testid="docx-unified-preview">
                            {docxRows.length === 0 && (
                                <div className="change-row same">
                                    <div className="change-row-head">Документ пуст</div>
                                    <div className="change-stack">
                                        <div className="change-col current"><span className="label">Содержимое:</span><p>—</p></div>
                                    </div>
                                </div>
                            )}
                            {docxRows.length > 0 && docxChangedRows.length === 0 && (
                                <div className="change-row same">
                                    <div className="change-row-head">Изменений не обнаружено</div>
                                    <div className="change-stack">
                                        <div className="change-col current"><span className="label">Текст:</span><p>Документы совпадают</p></div>
                                    </div>
                                </div>
                            )}
                            {docxChangedRows.map((row) => (
                                <div key={`docx-row-${row.index}`} className={`change-row ${row.changed ? 'changed' : 'same'}`}>
                                    <div className="change-row-head">Параграф {row.index}</div>
                                    <div className="change-stack">
                                        <div className="change-col current">
                                            <span className="label">Текст:</span>
                                            <p className="review-line">
                                                {row.inlineDiff.length > 0 ? row.inlineDiff.map((part, idx) => (
                                                    <span
                                                        key={`docx-diff-${row.index}-${idx}`}
                                                        className={`review-token ${part.type}`}
                                                    >
                                                        {part.text}
                                                    </span>
                                                )) : '—'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {isXlsx && (
                        <div className="xlsx-editor-window" data-testid="xlsx-unified-preview">
                            <div className="xlsx-toolbar">
                                <span className="xlsx-label">Лист:</span>
                                <select value={activeSheet} onChange={(e) => onSelectSheet(e.target.value)}>
                                    {sheetNames.map((sheetName) => (
                                        <option key={sheetName} value={sheetName}>{sheetName}</option>
                                    ))}
                                </select>
                                <span className="xlsx-changes-count">Изменено ячеек: {xlsxChangedRows.length}</span>
                            </div>

                            <div className="xlsx-grid-wrap">
                                <table className="xlsx-grid">
                                    <thead>
                                        <tr>
                                            <th className="corner" />
                                            {Array.from({ length: maxCol }, (_, i) => i + 1).map((col) => (
                                                <th key={`head-col-${col}`} className="col-header">{numberToColLabel(col)}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.from({ length: maxRow }, (_, i) => i + 1).map((row) => (
                                            <tr key={`row-${row}`}>
                                                <th className="row-header">{row}</th>
                                                {Array.from({ length: maxCol }, (_, j) => j + 1).map((col) => {
                                                    const cellAddress = `${numberToColLabel(col)}${row}`;
                                                    const beforeValue = beforeSheet.cells.get(cellAddress) || '';
                                                    const afterValue = afterSheet.cells.get(cellAddress) || '';
                                                    const isChanged = changedCells.has(cellAddress);

                                                    if (!isChanged) {
                                                        return <td key={cellAddress} title={cellAddress}>{afterValue || ''}</td>;
                                                    }

                                                    return (
                                                        <td key={cellAddress} className="changed-cell" title={cellAddress}>
                                                            <div className="cell-new">{afterValue || '—'}</div>
                                                            <div className="cell-old-label">было:</div>
                                                            <div className="cell-old">{beforeValue || '—'}</div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {!isDocx && !isXlsx && <p>Для этого формата предпросмотр пока не поддерживается</p>}
        </div>
    );
};

const DocumentEditCard = ({ message, userId }) => {
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState('');
    const [previewPayload, setPreviewPayload] = useState(null);
    const [actionError, setActionError] = useState('');
    const [selectedSheet, setSelectedSheet] = useState('');

    const editedMeta = useMemo(() => toEditedFileMeta(message?.editedFiles?.[0]), [message]);
    const sessionId = editedMeta?.sessionId || '';
    const editedFileName = useMemo(() => getFileNameFromUrl(editedMeta?.editedFileUrl || ''), [editedMeta?.editedFileUrl]);
    const editedExtLabel = useMemo(() => {
        if (editedFileName.toLowerCase().endsWith('.docx')) return 'Скачать .docx';
        if (editedFileName.toLowerCase().endsWith('.xlsx')) return 'Скачать .xlsx';
        return 'Скачать edited';
    }, [editedFileName]);

    const track = (eventName, data = {}) => {
        window.dispatchEvent(new CustomEvent('docs-edit-analytics', {
            detail: { event: eventName, ...data },
        }));
    };

    useEffect(() => {
        if (!sessionId) return;
        track('docs_edit_card_shown', { messageId: message.id, sessionId });
    }, [message.id, sessionId]);

    const openPreview = async () => {
        if (!userId) return;

        setActionError('');
        setPreviewLoading(true);
        setPreviewError('');
        track('docs_edit_preview_open', { sessionId: editedMeta.sessionId, version: editedMeta.version });

        try {
            const response = await withSingleRetry(() =>
                chatApi.getPreview(userId, editedMeta.sessionId, editedMeta.version)
            );
            setPreviewPayload(response.data);
        } catch (error) {
            const messageText = mapApiError(error?.response?.status);
            setPreviewError(messageText);
            track('docs_edit_error', { code: error?.response?.status || 'network', action: 'preview' });
        } finally {
            setPreviewLoading(false);
        }
    };

    useEffect(() => {
        if (!userId || !sessionId) return;
        openPreview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, sessionId]);

    useEffect(() => {
        if (previewPayload?.ext !== '.xlsx') return;
        const names = getSheetNames(previewPayload);
        if (names.length === 0) {
            setSelectedSheet('');
            return;
        }
        if (!names.includes(selectedSheet)) {
            setSelectedSheet(names[0]);
        }
    }, [previewPayload, selectedSheet]);

    if (!sessionId) {
        return null;
    }

    const downloadFile = async (fileUrl, downloadKind) => {
        if (!userId) return;
        if (!fileUrl) return;

        setActionError('');
        track('docs_edit_download_clicked', { kind: downloadKind, sessionId: editedMeta.sessionId });

        try {
            const response = await withSingleRetry(() =>
                chatApi.downloadEditedFile(userId, message.id, fileUrl)
            );
            const nameFromHeader = getContentDispositionFileName(response.headers);
            const fallbackName = getFileNameFromUrl(fileUrl);
            triggerBrowserDownload(response.data, nameFromHeader || fallbackName);
        } catch (error) {
            const messageText = mapApiError(error?.response?.status);
            setActionError(messageText);
            track('docs_edit_error', { code: error?.response?.status || 'network', action: 'download' });
        }
    };

    return (
        <div className="docs-edit-card" data-testid="docs-edit-card">
            <div className="docs-edit-head">
                <div>
                    <h4>Редактирование документа · v{editedMeta.version}</h4>
                </div>
                <span className="docs-badge">DOCX/XLSX</span>
            </div>

            {actionError && <p className="docs-edit-error">{actionError}</p>}

            <InlinePreview
                loading={previewLoading}
                error={previewError}
                preview={previewPayload}
                selectedSheet={selectedSheet}
                onSelectSheet={setSelectedSheet}
                onDownloadEdited={() => downloadFile(editedMeta.editedFileUrl, 'edited')}
                onDownloadReview={editedMeta.reviewFileUrl ? () => downloadFile(editedMeta.reviewFileUrl, 'review') : null}
                onDownloadProtocol={editedMeta.protocolFileUrl ? () => downloadFile(editedMeta.protocolFileUrl, 'protocol') : null}
                editedDownloadLabel={editedExtLabel}
            />
        </div>
    );
};

export default DocumentEditCard;
